import { useCallback, useSyncExternalStore } from 'react';

/**
 * Tracks which hpKeys' persisted unsaved-step-edit records have finished reading from IndexedDB.
 *
 * The record is read once, imperatively, by useHangingProtocol's init pass, which must not treat "not
 * loaded yet" as "no unsaved edits" -- doing so would discard the very edits this feature restores.
 * localForagePersisterEffect's onHydrated option reports the read settling, but it is supplied from
 * inside the atom family's `effects` option, where there is no way to write another atom. Hence a
 * module-level registry surfaced through useSyncExternalStore rather than Recoil.
 *
 * Entries are never removed. A hydrated key stays hydrated for the life of the page, which is exactly
 * the semantics the gate wants, and the set is bounded by the number of distinct case-and-HP pairs a
 * single session visits.
 */
const hydratedHPKeys = new Set<string>();
const subscribers = new Set<() => void>();

export function markUnsavedStepEditsHydrated(hpKey: string): void {
  if (hydratedHPKeys.has(hpKey)) return;
  hydratedHPKeys.add(hpKey);
  for (const notify of subscribers) notify();
}

function subscribe(onStoreChange: () => void): () => void {
  subscribers.add(onStoreChange);
  return () => {
    subscribers.delete(onStoreChange);
  };
}

/**
 * True once `hpKey`'s persisted record has been read, or immediately when there is no key to wait for.
 * Callers gate their one-shot read of the record on this.
 */
export function useIsUnsavedStepEditsHydrated(hpKey: Nullable<string>): boolean {
  const getSnapshot = useCallback(
    () => (hpKey == null ? true : hydratedHPKeys.has(hpKey)),
    [hpKey]
  );
  return useSyncExternalStore(subscribe, getSnapshot);
}

/** Test-only. Clears the registry so each test starts from an unhydrated state. */
export function __resetUnsavedStepEditsHydration(): void {
  hydratedHPKeys.clear();
  subscribers.clear();
}
